import { useState, useEffect, useRef, useCallback } from "react";
import {
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Modal,
  Link,
  Alert,
  Snackbar,
  Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useSelector } from "react-redux";
import { IndianStates } from "../../assets/IndianStates";
import VerifiedIcon from "@mui/icons-material/Verified";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import ClientApiModule from "../../components/ClientApi/api";
import debounce from "lodash.debounce";
import BulkUploadComponent from "./components/BulkUploadComponent";
import { BASE_URL } from "../../components/config/config";
import axios from "axios";

const AVAILABLE_TIME_PREFERENCES = [
  "IMMEDIATELY",
  "WITHIN_24_HOURS",
  "WITHIN_48_HOURS",
  "SPECIFIC_DATE_AND_TIME",
];

const AddClientTicketById = () => {
  const clientState = useSelector((state) => state.clientReducer);
  const imageInputRef = useRef(null);
  const [isFetchingPincode, setIsFetchingPincode] = useState(false);
  const [clientsList, setClientsList] = useState([]);

  const [formData, setFormData] = useState({
    client_obj_id: "",
    service_provided_for: "",
    service_provided_type: "",
    subCategoryName: "",
    subCategories: [],
    specific_requirement: "",
    count_of_accessories: "1",
    model: "",
    personal_details: {
      primary_phone: {
        country_code: "+91",
        mobile_number: "",
      },
      alternate_phone: {
        country_code: "+91",
        mobile_number: "",
      },
      name: "",
    },
    time_preference: {
      time_preference_type: "WITHIN_48_HOURS",
    },
    offers_applied: {
      offer_code: "",
    },
    address_details: {
      longitude: 0,
      latitude: 0,
      house_number: "",
      city: "",
      state: "",
      pincode: "",
      additional_pincode: "",
      country: "INDIA",
      google_geo_location: "",
      locality: "",
    },
    client_onsite_pictures: [],
  });

  const [state, setState] = useState({
    clientsList: [],
    clientServices: [],
    isRegistering: false,
    TicketModalOpen: false,
    TicketModalData: [],
    selectedImages: [],
    imagePreviews: [],
    isLoadingServices: false,
    isLoadingSubCategories: false,
  });

  const [toast, setToast] = useState({
    open: false,
    msg: "",
    duration: 5000,
    type: "",
  });

  // Get current client's QR ID
  const currentQrId = clientState?.clientDetails?.qr_details?.qr_id;

  useEffect(() => {
    if (currentQrId) {
      getClientServices(currentQrId);
    }
  }, [currentQrId]);

  const fetchPincodeDetails = useCallback(async (pincode) => {
    if (pincode.length !== 6) return;

    setIsFetchingPincode(true);
    try {
      const response = await fetch(
        `https://api.postalpincode.in/pincode/${pincode}`
      );
      const data = await response.json();

      if (
        data &&
        data[0]?.Status === "Success" &&
        data[0]?.PostOffice?.length > 0
      ) {
        const firstPostOffice = data[0].PostOffice[0];

        setFormData((prev) => ({
          ...prev,
          address_details: {
            ...prev.address_details,
            city: firstPostOffice.District || prev.address_details.city,
            state: firstPostOffice.State || prev.address_details.state,
            pincode: pincode,
          },
        }));
      } else {
        showDialog(
          true,
          "warning",
          "Could not find location details for this pincode"
        );
      }
    } catch (error) {
      console.error("Error fetching pincode details:", error);
      showDialog(
        true,
        "error",
        "Failed to fetch location details. Please enter manually."
      );
    } finally {
      setIsFetchingPincode(false);
    }
  }, []);

  const debouncedFetchPincode = useCallback(
    debounce((pincode) => {
      fetchPincodeDetails(pincode);
    }, 800),
    []
  );

  useEffect(() => {
    return () => {
      debouncedFetchPincode.cancel();
    };
  }, [debouncedFetchPincode]);

  const onChangeSpecificDateAndTime = (newValue) => {
    setFormData((prev) => ({
      ...prev,
      time_preference: {
        ...prev.time_preference,
        specific_date_time: newValue,
        specific_date_time_time_stamp: newValue.getTime(),
      },
    }));
  };

  const getClientServices = async (qrId) => {
    if (!qrId) return;
    try {
      setState((prev) => ({ ...prev, isLoadingServices: true }));
      const res = await new ClientApiModule().getAllClientServices(qrId);

      const servicesData = res.data?.data || res.data;

      if (Array.isArray(servicesData)) {
        const services = servicesData.map((service) => ({
          _id: service._id,
          service_name:
            service._id === "other" ? service.name : service.service_name,
          booking_link: service.booking_link,
          isSpecial: service._id === "other",
        }));

        setState((prev) => ({
          ...prev,
          clientServices: services,
          isLoadingServices: false,
        }));
      } else {
        setState((prev) => ({
          ...prev,
          clientServices: [],
          isLoadingServices: false,
        }));
      }
    } catch (err) {
      setState((prev) => ({
        ...prev,
        clientServices: [],
        isLoadingServices: false,
      }));
    }
  };

  const showDialog = (open, type, msg) => {
    setToast({
      open,
      msg,
      duration: 5000,
      type,
    });
  };

  const closeDialog = () => {
    setToast({
      open: false,
      msg: "",
      duration: 5000,
      type: "",
    });
  };

  const buildSpecificRequirement = (subCategoryName, model, countOfAccessories) => {
    const parts = [];
    if (subCategoryName) parts.push(subCategoryName);
    if (model) parts.push(model);
    const qty = parseInt(countOfAccessories, 10) || 1;
    if (qty >= 1) parts.push(`Qty: ${qty}`);
    return parts.join(" ");
  };

  const formatSubCategoryDisplay = (subCat, quantity) => {
    if (!subCat) return "";
    const qty = parseInt(quantity, 10) || 1;
    const price = subCat.price * qty;
    return `${subCat.name}${subCat.name2 ? ` - ${subCat.name2}` : ""}${
      price ? ` @ ₹${price}` : ""
    }`;
  };

  const fetchSubCategories = async (primaryServiceId, clientId) => {
    if (!primaryServiceId || primaryServiceId === "other" || !clientId) return;

    setState((prev) => ({ ...prev, isLoadingSubCategories: true }));

    try {
      const [serviceAreaRes, categoryPriceRes] = await Promise.all([
        axios.get(
          `${BASE_URL}client/get-category-based-client-and-service?primary_services=${primaryServiceId}&client_id=${clientId}`
        ),
        axios.get(
          `${BASE_URL}admin/get-category-based-service?primary_services=${primaryServiceId}`
        ),
      ]);

      let categoryIds = [];
      if (
        serviceAreaRes.data?.success &&
        serviceAreaRes.data?.data?.formatted?.length > 0
      ) {
        const serviceData = serviceAreaRes.data.data.formatted[0];
        const categoryDetails = serviceData.serviceAreas?.categories || [];
        categoryIds = categoryDetails.map((category) => category.category_id);
      }

      let subCategories = [];
      if (
        categoryPriceRes.data?.success &&
        categoryPriceRes.data?.data?.names
      ) {
        subCategories = categoryPriceRes.data.data.names
          .filter((category) => categoryIds.includes(category._id))
          .map((category) => ({
            id: category._id,
            name: category.label || "",
            name2: category.label2 || "",
            price: category.category_service_prize || 0,
          }));
      }

      setFormData((prev) => {
        const quantity = parseInt(prev.count_of_accessories, 10) || 1;

        if (subCategories.length > 0) {
          const firstSubCategory = subCategories[0];
          const subCategoryDisplay = formatSubCategoryDisplay(
            firstSubCategory,
            quantity
          );
          return {
            ...prev,
            subCategories,
            service_provided_type: firstSubCategory.id,
            subCategoryName: subCategoryDisplay,
            specific_requirement: buildSpecificRequirement(
              subCategoryDisplay,
              prev.model,
              prev.count_of_accessories
            ),
          };
        }

        return {
          ...prev,
          subCategories,
          service_provided_type: "",
          subCategoryName: "",
          specific_requirement: buildSpecificRequirement(
            "",
            prev.model,
            prev.count_of_accessories
          ),
        };
      });

      if (subCategories.length === 0) {
        showDialog(
          true,
          "warning",
          "No sub-categories available for this service"
        );
      }
    } catch (err) {
      console.error("Error fetching sub-categories:", err);
      showDialog(true, "error", "Failed to load sub-categories");
    } finally {
      setState((prev) => ({ ...prev, isLoadingSubCategories: false }));
    }
  };

  const handleSubCategoryChange = (e) => {
    const subCategoryId = e.target.value;
    setFormData((prev) => {
      const selectedSubCategory = prev.subCategories?.find(
        (subCat) => subCat.id === subCategoryId
      );
      const quantity = parseInt(prev.count_of_accessories, 10) || 1;
      const subCategoryDisplay = selectedSubCategory
        ? formatSubCategoryDisplay(selectedSubCategory, quantity)
        : "";
      return {
        ...prev,
        service_provided_type: subCategoryId,
        subCategoryName: subCategoryDisplay,
        specific_requirement: buildSpecificRequirement(
          subCategoryDisplay,
          prev.model,
          prev.count_of_accessories
        ),
      };
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "model") {
      setFormData((prev) => ({
        ...prev,
        model: value,
        specific_requirement: buildSpecificRequirement(
          prev.subCategoryName,
          value,
          prev.count_of_accessories
        ),
      }));
      return;
    }

    if (name === "count_of_accessories") {
      setFormData((prev) => {
        const selectedSubCategory = prev.subCategories?.find(
          (subCat) => subCat.id === prev.service_provided_type
        );
        const subCategoryDisplay = selectedSubCategory
          ? formatSubCategoryDisplay(selectedSubCategory, value)
          : prev.subCategoryName;
        return {
          ...prev,
          count_of_accessories: value,
          subCategoryName: subCategoryDisplay,
          specific_requirement: buildSpecificRequirement(
            subCategoryDisplay,
            prev.model,
            value
          ),
        };
      });
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      address_details: {
        ...prev.address_details,
        [name]: value,
      },
    }));

    if (name === "pincode") {
      if (value.length === 6) {
        debouncedFetchPincode(value);
      } else if (value.length < 6) {
        setFormData((prev) => ({
          ...prev,
          address_details: {
            ...prev.address_details,
            city: "",
            state: "",
          },
        }));
      }
    }
  };

  const handlePersonalDetailsChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      personal_details: {
        ...prev.personal_details,
        [name]: value,
      },
    }));
  };

  const handlePhoneChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      personal_details: {
        ...prev.personal_details,
        primary_phone: {
          ...prev.personal_details.primary_phone,
          [name]: value,
        },
      },
    }));
  };

  const handleAlternatePhoneChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      personal_details: {
        ...prev.personal_details,
        alternate_phone: {
          ...prev.personal_details.alternate_phone,
          [name]: value,
        },
      },
    }));
  };

  const handleTimePreferenceChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      time_preference: {
        ...prev.time_preference,
        time_preference_type: value,
      },
    }));
  };

  const handleImageChange = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const newImages = Array.from(files);
      const newPreviews = newImages.map((file) => URL.createObjectURL(file));

      setState((prev) => ({
        ...prev,
        selectedImages: [...prev.selectedImages, ...newImages],
        imagePreviews: [...prev.imagePreviews, ...newPreviews],
      }));
    }
  };

  const removeImage = (index) => {
    const newImages = [...state.selectedImages];
    const newPreviews = [...state.imagePreviews];

    newImages.splice(index, 1);
    newPreviews.splice(index, 1);

    setState((prev) => ({
      ...prev,
      selectedImages: newImages,
      imagePreviews: newPreviews,
    }));
  };

  const handleRegister = async () => {
    const {
      service_provided_for,
      service_provided_type,
      specific_requirement,
      count_of_accessories = 1,
      model,
      personal_details,
      time_preference,
      address_details,
    } = formData;

    const selectedPrimary = state.clientServices.find(
      (s) => s._id === service_provided_for
    );
    const needsSubCategory = selectedPrimary && !selectedPrimary.isSpecial;

    const required = [
      service_provided_for,
      ...(needsSubCategory ? [service_provided_type] : []),
      count_of_accessories,
      model,
      personal_details?.name,
      personal_details?.primary_phone?.mobile_number,
      address_details?.house_number,
      address_details?.city,
      address_details?.state,
      address_details?.pincode,
    ];

    if (required.some((v) => !v)) {
      showDialog(true, "error", "Please fill in all required details");
      return;
    }

    if (personal_details.primary_phone.mobile_number.length !== 10) {
      showDialog(true, "error", "Please enter a valid 10-digit phone number");
      return;
    }

    if (address_details.pincode.length !== 6) {
      showDialog(true, "error", "Please enter a valid 6-digit pincode");
      return;
    }

    const fullRequirement = buildSpecificRequirement(
      formData.subCategoryName,
      model,
      count_of_accessories
    );

    const payload = {
      client_obj_id: clientState?.clientDetails?._id ?? "",
      service_provided_for,
      service_provided_type,
      specific_requirement: fullRequirement || specific_requirement,
      count_of_accessories: Number(count_of_accessories),
      model,
      personal_details: {
        name: personal_details.name,
        primary_phone: { ...personal_details.primary_phone },
        alternate_phone: { ...personal_details.alternate_phone },
      },
      time_preference: {
        ...time_preference,
        ...(time_preference.time_preference_type ===
          "SPECIFIC_DATE_AND_TIME" && {
          specific_date_time: time_preference.specific_date_time?.toISOString(),
          specific_date_time_time_stamp:
            time_preference.specific_date_time_time_stamp,
        }),
      },
      offers_applied: { offer_code: clientState?.clientDetails?.client_name },
      address_details: {
        ...address_details,
        locality: address_details.locality,
        additional_pincode:
          address_details.additional_pincode || address_details.pincode,
        google_geo_location: address_details.google_geo_location || "",
      },
      client_onsite_pictures: state.imagePreviews,
    };

    setState((p) => ({ ...p, isRegistering: true }));

    try {
      const res = await new ClientApiModule().clientCreateTicketDashboard(
        payload
      );

      // Handle different response structures
      const responseData = res.data || res;

      // Check for no service available case
      const isNoServiceAvailable =
        responseData?.success === 0 &&
        responseData?.status === 410 &&
        responseData?.message?.includes("not available in your pin code");

      if (isNoServiceAvailable) {
        // Handle "no service available" case
        showDialog(true, "error", responseData.message);
        setState((p) => ({ ...p, isRegistering: false }));
        handleResetValues(); // Reset form for no service available case
      } else if (
        responseData?.success === 1 ||
        responseData?.success === true ||
        responseData?.ticket_id
      ) {
        // Handle success case - added ticket_id check as additional success indicator
        showDialog(true, "success", "Ticket created successfully!");
        setState((p) => ({
          ...p,
          TicketModalData: responseData.data || responseData,
          TicketModalOpen: true,
          isRegistering: false,
        }));
        handleResetValues();
      } else {
        // Handle other errors
        showDialog(
          true,
          "error",
          responseData?.message || "Something went wrong"
        );
        setState((p) => ({ ...p, isRegistering: false }));
      }
    } catch (err) {
      // First, check if this is actually a successful response that got caught as an error
      const errorResponse = err.response?.data || err;

      // Check if the error response indicates success (sometimes APIs return success in error format)
      if (
        errorResponse?.success === 1 ||
        errorResponse?.success === true ||
        errorResponse?.ticket_id
      ) {
        // This is actually a success case that was caught as an error
        showDialog(true, "success", "Ticket created successfully!");
        setState((p) => ({
          ...p,
          TicketModalData: errorResponse.data || errorResponse,
          TicketModalOpen: true,
          isRegistering: false,
        }));
        handleResetValues();
      }
      // Check if the error response indicates no service available
      else if (
        errorResponse?.success === 0 &&
        errorResponse?.status === 410 &&
        errorResponse?.message?.includes("not available in your pin code")
      ) {
        showDialog(true, "error", errorResponse.message);
        setState((p) => ({ ...p, isRegistering: false }));
        handleResetValues();
      }
      // Handle actual errors
      else {
        const errorMessage =
          errorResponse?.message || err.message || "Something went wrong";
        showDialog(true, "error", errorMessage);
        setState((p) => ({ ...p, isRegistering: false }));
      }
    }
  };

  const handleResetValues = () => {
    setFormData({
      client_obj_id: clientState?.clientDetails?._id || "",
      service_provided_for: "",
      service_provided_type: "",
      subCategoryName: "",
      subCategories: [],
      specific_requirement: "",
      count_of_accessories: "1",
      model: "",
      personal_details: {
        primary_phone: {
          country_code: "+91",
          mobile_number: "",
        },
        alternate_phone: {
          country_code: "+91",
          mobile_number: "",
        },
        name: "",
      },
      time_preference: {
        time_preference_type: "IMMEDIATELY",
      },
      offers_applied: {
        offer_code: "",
      },
      address_details: {
        longitude: 0,
        latitude: 0,
        house_number: "",
        city: "",
        state: "",
        pincode: "",
        additional_pincode: "",
        country: "INDIA",
        google_geo_location: "",
        locality: "",
      },
      client_onsite_pictures: [],
    });

    setState((prev) => ({
      ...prev,
      selectedImages: [],
      imagePreviews: [],
    }));
  };

  const openWhatsapp = (number, id, name) => {
    if (number) {
      const formattedPhoneNumber = number.replace(/[^\d]/g, "");
      const a = `https://app.onit.services/#/ServiceStatus/?${id}`;
      const message = `Dear ${name} request Onit has been created, Please click on the link to check status: ${a} Download *Onit Services* from playstore and get more Benefits and Offers`;

      const encodedMessage = encodeURIComponent(message);
      window.open(
        `https://web.whatsapp.com/send?phone=${formattedPhoneNumber}&text=${encodedMessage}`,
        "_blank"
      );
    }
  };

  const handleClose = () => {
    setState((prev) => ({ ...prev, TicketModalOpen: false }));
  };

  const { TicketModalData } = state;

  // Bulk Upload Component

  const fetchClientsList = async () => {
    try {
      // You might need to adjust this based on your API
      const res = await new ClientApiModule().getAllClients(); // or appropriate method
      let parsedData = [];
      if (res && res.data && res.data.length > 0) {
        parsedData = res.data;
      }
      setClientsList(parsedData);
    } catch (err) {
      console.log("Error fetching clients list:", err);
    }
  };

  useEffect(() => {
    fetchClientsList();
  }, []);

  return (
    <div className="min-h-screen bg-teal-50 p-2 md:p-4">
      {/* Success Modal */}
      <Modal
        keepMounted
        open={state.TicketModalOpen}
        onClose={handleClose}
        aria-labelledby="keep-mounted-modal-title"
        aria-describedby="keep-mounted-modal-description"
      >
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-lg shadow-xl p-6 outline-none">
          <div className="flex items-center mb-4">
            <VerifiedIcon className="text-teal-600 mr-2" fontSize="large" />
            <Typography variant="h6" className="font-bold text-black">
              Hurray! Your Request has been created
            </Typography>
          </div>

          <List className="w-full">
            <ListItem className="px-0">
              <ListItemText
                primary="Ticket ID"
                primaryTypographyProps={{
                  className: "font-medium text-teal-700",
                }}
              />
              <Typography className="text-gray-900">
                {TicketModalData?.ticket_id || "N/A"}
              </Typography>
            </ListItem>

            <Divider className="bg-teal-200" />

            <ListItem className="px-0">
              <ListItemText
                primary="Status"
                primaryTypographyProps={{
                  className: "font-medium text-teal-700",
                }}
              />
              <Typography
                className={`font-medium ${
                  TicketModalData?.ticket_status === "COMPLETED"
                    ? "text-gray-800"
                    : "text-gray-900"
                }`}
              >
                {TicketModalData?.ticket_status || "CREATED"}
              </Typography>
            </ListItem>

            <Divider className="bg-teal-200" />

            <ListItem className="px-0">
              <ListItemText
                primary="Created At"
                primaryTypographyProps={{
                  className: "font-medium text-teal-700",
                }}
              />
              <Typography className="text-gray-900">
                {TicketModalData?.createdAt
                  ? new Date(TicketModalData.createdAt).toLocaleString()
                  : "Just now"}
              </Typography>
            </ListItem>

            <Divider className="bg-teal-200" />

            <ListItem className="px-0">
              <ListItemText
                primary="Tracking Link"
                primaryTypographyProps={{
                  className: "font-medium text-teal-700",
                }}
              />
              <div className="flex items-center">
                <Link
                  href={`https://app.onit.services/#/ServiceStatus/?${TicketModalData?._id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-teal-600 hover:text-teal-800 hover:underline font-medium"
                >
                  Track Service
                </Link>
              </div>
            </ListItem>

            <ListItem className="justify-center px-0 pt-4 gap-16">
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#0d9488",
                  "&:hover": {
                    backgroundColor: "#0f766e",
                  },
                }}
                startIcon={<WhatsAppIcon />}
                onClick={() =>
                  openWhatsapp(
                    TicketModalData?.personal_details?.primary_phone
                      ?.mobile_number,
                    TicketModalData?._id,
                    TicketModalData?.personal_details?.name
                  )
                }
              >
                Share via WhatsApp
              </Button>
              <Button
                variant="outlined"
                onClick={handleClose}
                sx={{
                  color: "#0d9488",
                  borderColor: "#0d9488",
                  "&:hover": {
                    borderColor: "#0f766e",
                    backgroundColor: "#f0fdfa",
                  },
                }}
              >
                Close
              </Button>
            </ListItem>
          </List>
        </div>
      </Modal>

      {/* Toast Notification */}
      {toast.open && (
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          open={toast.open}
          autoHideDuration={toast.duration}
          onClose={closeDialog}
        >
          <Alert onClose={closeDialog} severity={toast.type} className="w-full">
            {toast.msg}
          </Alert>
        </Snackbar>
      )}

      {/* Main Form */}
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-4xl font-bold text-gray-800 mb-3">
            Add Service Request
          </h2>
          <p className="text-gray-600 text-lg">
            Fill in the details below to create a new service request
          </p>
        </div>

        {/* Add Bulk Upload Component Here */}
        <BulkUploadComponent
          clientsList={clientsList}
          currentClientId={clientState?.clientDetails?.client_id}
          BASE_URL={BASE_URL}
          onShowDialog={showDialog}
        />

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mt-4">
          <div className="p-6 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Service Required */}
              <div>
                <label className="block text-md font-medium text-black mb-2">
                  Service You Required
                </label>
                <select
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg cursor-pointer"
                  value={formData.service_provided_for}
                  onChange={(e) => {
                    const selectedValue = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      service_provided_for: selectedValue,
                      service_provided_type: "",
                      subCategoryName: "",
                      subCategories: [],
                      specific_requirement: "",
                    }));

                    const selectedService = state.clientServices.find(
                      (s) => s._id === selectedValue
                    );

                    if (
                      selectedService?.isSpecial &&
                      selectedService.booking_link
                    ) {
                      window.open(selectedService.booking_link, "_blank");
                      return;
                    }

                    const clientId = clientState?.clientDetails?._id;
                    if (selectedValue && selectedValue !== "other" && clientId) {
                      fetchSubCategories(selectedValue, clientId);
                    }
                  }}
                  name="service_provided_for"
                >
                  <option value="" className="text-gray-700">
                    Select a service
                  </option>
                  {state.clientServices.length > 0 ? (
                    state.clientServices.map((service) => (
                      <option
                        key={service._id}
                        value={service._id}
                        className={
                          service.isSpecial
                            ? "text-blue-600 font-medium"
                            : "text-gray-700"
                        }
                      >
                        {service.service_name}
                        {service.isSpecial && " (Click to book other services)"}
                      </option>
                    ))
                  ) : (
                    <option disabled value="" className="text-gray-700">
                      {state.isLoadingServices
                        ? "Loading services..."
                        : "No services available"}
                    </option>
                  )}
                </select>
              </div>

              {/* Sub-category (Category ObjectId for service_provided_type) */}
              <div>
                <label className="block text-md font-medium text-black mb-2">
                  Service type
                </label>
                {formData.service_provided_for &&
                !state.clientServices.find(
                  (s) => s._id === formData.service_provided_for
                )?.isSpecial ? (
                  <select
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg cursor-pointer"
                    value={formData.service_provided_type}
                    onChange={handleSubCategoryChange}
                    name="service_provided_type"
                    disabled={
                      state.isLoadingSubCategories ||
                      !formData.service_provided_for
                    }
                  >
                    <option value="" className="text-gray-600">
                      {state.isLoadingSubCategories
                        ? "Loading service types..."
                        : "Select a service type"}
                    </option>
                    {formData.subCategories?.map((subCat) => (
                      <option key={subCat.id} value={subCat.id}>
                        {`${subCat.name}${
                          subCat.name2 ? ` - ${subCat.name2}` : ""
                        }${subCat.price ? ` @ ₹${subCat.price}` : " @ ₹0"}`}
                      </option>
                    ))}
                  </select>
                ) : (
                  <select
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg cursor-not-allowed bg-gray-50 text-gray-500"
                    disabled
                    value=""
                  >
                    <option value="">Select a primary service first</option>
                  </select>
                )}
              </div>

              {/* Model */}
              <div>
                <label className="block text-md font-medium text-black mb-2">
                  Model
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg"
                  name="model"
                  value={formData.model}
                  onChange={handleChange}
                  maxLength={50}
                />
              </div>

              {/* Image Upload */}
              <div className="col-span-1 md:col-span-2">
                <label className="block text-md font-medium text-black mb-3">
                  Upload Images
                </label>
                <input
                  accept="image/*"
                  className="hidden"
                  id="image-upload"
                  type="file"
                  onChange={handleImageChange}
                  multiple
                  ref={imageInputRef}
                />
                <div className="flex flex-col space-y-4">
                  <label
                    htmlFor="image-upload"
                    className="flex items-center justify-center px-4 py-3 border-2  border-gray-300 rounded-lg cursor-pointer hover:bg-gray-100"
                  >
                    <CloudUploadIcon className="text-teal-500 mr-3" />
                    <span className="text-sm font-medium text-teal-700">
                      Choose Images
                    </span>
                  </label>

                  {state.imagePreviews.length > 0 && (
                    <div className="flex flex-wrap gap-4">
                      {state.imagePreviews.map((preview, index) => (
                        <div
                          key={index}
                          className="relative w-24 h-24 rounded-lg overflow-hidden shadow-sm border-2 border-gray-300"
                        >
                          <img
                            src={preview}
                            alt={`Preview ${index}`}
                            className="w-full h-full object-cover"
                          />
                          <button
                            onClick={() => removeImage(index)}
                            className="absolute top-1 right-1 bg-white rounded-full p-1 shadow-md hover:bg-teal-50 transition-colors border border-gray-300"
                          >
                            <CloseIcon className="text-teal-600 text-sm" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Contact Person */}
              <div>
                <label className="block text-md font-medium text-black mb-2">
                  Name (Contact Person)
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg"
                  name="name"
                  value={formData.personal_details.name}
                  onChange={handlePersonalDetailsChange}
                  maxLength={30}
                />
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-md font-medium text-black mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <span className="text-gray-600 font-medium">+91</span>
                  </div>
                  <input
                    type="tel"
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-lg"
                    name="mobile_number"
                    value={
                      formData.personal_details.primary_phone.mobile_number
                    }
                    onChange={handlePhoneChange}
                    maxLength={10}
                  />
                </div>
              </div>

              {/* Alternate Phone */}
              <div>
                <label className="block text-md font-medium text-black mb-2">
                  Alternate Phone Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <span className="text-gray-600 font-medium">+91</span>
                  </div>
                  <input
                    type="tel"
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-lg"
                    name="mobile_number"
                    value={
                      formData.personal_details.alternate_phone.mobile_number
                    }
                    onChange={handleAlternatePhoneChange}
                    maxLength={10}
                  />
                </div>
              </div>

              {/* House Number */}
              <div>
                <label className="block text-md font-medium text-black mb-2">
                  House Number / Street
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg"
                  name="house_number"
                  value={formData.address_details.house_number}
                  onChange={handleAddressChange}
                  maxLength={200}
                />
              </div>

              {/* Locality */}
              <div>
                <label className="block text-md font-medium text-black mb-2">
                  Locality
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg"
                  name="locality"
                  value={formData.address_details.locality}
                  onChange={handleAddressChange}
                  maxLength={100}
                />
              </div>

              {/* Pincode */}
              <div>
                <label className="block text-md font-medium text-black mb-2">
                  Pincode
                </label>
                <div className="relative">
                  <input
                    type="text"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg"
                    name="pincode"
                    value={formData.address_details.pincode}
                    onChange={handleAddressChange}
                    maxLength={6}
                  />
                  {isFetchingPincode && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <div className="w-5 h-5 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
              </div>

              {/* City */}
              <div>
                <label className="block text-md font-medium text-black mb-2">
                  City
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg"
                  name="city"
                  value={formData.address_details.city}
                  onChange={handleAddressChange}
                  maxLength={30}
                  disabled={isFetchingPincode}
                />
              </div>

              {/* State */}
              <div>
                <label className="block text-md font-medium text-black mb-2">
                  State
                </label>
                <select
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg"
                  name="state"
                  value={formData.address_details.state}
                  onChange={handleAddressChange}
                  disabled={isFetchingPincode}
                >
                  <option value="" className="text-black">
                    Select a state
                  </option>
                  {IndianStates.map((state) => (
                    <option
                      key={state.value}
                      value={state.value}
                      className="text-black"
                    >
                      {state.value}
                    </option>
                  ))}
                </select>
              </div>

              {/* Country */}
              <div>
                <label className="block text-md font-medium text-black mb-2">
                  Country
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg"
                  name="country"
                  value={formData.address_details.country}
                  onChange={handleAddressChange}
                  maxLength={30}
                  disabled
                />
              </div>

              {/* Time Preference */}
              <div>
                <label className="block text-md font-medium text-black mb-2">
                  Time Preference
                </label>
                <select
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg cursor-pointer"
                  name="time_preference_type"
                  value={formData.time_preference.time_preference_type}
                  onChange={handleTimePreferenceChange}
                >
                  {AVAILABLE_TIME_PREFERENCES.map((preference) => (
                    <option
                      key={preference}
                      value={preference}
                      className="text-black"
                    >
                      {preference.replace(/_/g, " ")}
                    </option>
                  ))}
                </select>
              </div>

              {/* Specific Date Time */}
              {formData.time_preference.time_preference_type ===
                "SPECIFIC_DATE_AND_TIME" && (
                <div>
                  <label className="block text-sm font-medium text-teal-700 mb-2">
                    Select date and time
                  </label>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DateTimePicker
                      renderInput={(props) => (
                        <div className="relative">
                          <input
                            {...props}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white transition-colors"
                          />
                        </div>
                      )}
                      value={formData.time_preference.specific_date_time}
                      onChange={onChangeSpecificDateAndTime}
                      minDate={new Date()}
                      minTime={new Date(0, 0, 0, 8)}
                      maxTime={new Date(0, 0, 0, 20)}
                    />
                  </LocalizationProvider>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="mt-8 flex justify-end">
              <button
                onClick={handleRegister}
                disabled={state.isRegistering}
                className="px-8 py-4 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-lg shadow-lg transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-teal-300 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed flex items-center text-lg"
              >
                {state.isRegistering ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  "Submit Request"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddClientTicketById;
